import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import useConfirmModal from '../hooks/useConfirmModal';
import {
  applyCustomerScreen,
  fetchCustomerScreen,
  removeCustomerScreenLogo,
  uploadCustomerScreenLogo,
  customerScreenErrorMessage,
} from '../utils/customerDisplayOptions';
import { LogoError } from '../utils/customerDisplay';

const SOURCE_LABELS = {
  custom: 'Custom logo for the POS',
  restaurant: 'Business details logo',
  none: 'No logo set - the POS icon is shown',
};

const useOnline = () => {
  const [online, setOnline] = useState(typeof navigator === 'undefined' || navigator.onLine !== false);
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return online;
};

/**
 * Settings > "Customer screen": the idle logo shown on the C20 Pro second screen. Restaurant-wide
 * and stored on the server (every terminal of the restaurant shows the same picture), so unlike the
 * device-only cards it needs a connection to change. Rendered independently of the server-driven
 * settings list, so it is there whether or not that list has loaded.
 */
const CustomerScreenCard = ({ downscale }) => {
  const online = useOnline();
  const [screen, setScreen] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const fileInput = useRef(null);
  const { confirm, ConfirmModalRenderer } = useConfirmModal();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchCustomerScreen();
        if (cancelled) return;
        setScreen(data);
        applyCustomerScreen(data);
      } catch (e) {
        if (!cancelled) setLoadFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setError('');
    setBusy(true);
    try {
      const data = await uploadCustomerScreenLogo(file, downscale);
      setScreen(data);
      setLoadFailed(false);
      toast.success('Customer screen logo updated');
    } catch (err) {
      setError(err instanceof LogoError ? err.message : customerScreenErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    const ok = await confirm({
      title: 'Remove custom logo',
      message: 'The customer screen will go back to showing the logo from Business details. Continue?',
      confirmText: 'Remove',
      confirmClass: 'btn-danger',
    });
    if (!ok) return;
    setError('');
    setBusy(true);
    try {
      const data = await removeCustomerScreenLogo();
      setScreen(data);
      toast.success('Custom logo removed');
    } catch (err) {
      setError(customerScreenErrorMessage(err, 'Could not remove the logo. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  const logoUrl = screen?.display_logo_url || null;
  const source = screen?.logo_source || (logoUrl ? 'restaurant' : 'none');
  const controlsDisabled = !online || busy || loadFailed || !screen;

  return (
    <div className="cardStyle mb-4">
      <div className="cardBody">
        <h5 className="mb-1">Customer screen</h5>
        <p className="mb-3" style={{ opacity: 0.7, fontSize: 13 }}>
          The logo the customer screen shows while no order is being taken. Applies to every
          terminal of this restaurant. By default it is the logo from Business details; upload a
          different one if it does not look right on a dark screen.
        </p>

        <div className="d-flex align-items-center gap-3 flex-wrap">
          <div
            data-testid="customer-screen-preview"
            style={{
              background: '#111',
              borderRadius: 8,
              width: 160,
              height: 90,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            {logoUrl ? (
              <img src={logoUrl} alt="Customer screen logo" style={{ maxWidth: '100%', maxHeight: '100%' }} />
            ) : (
              <span style={{ color: '#aaa', fontSize: 12 }}>POS icon</span>
            )}
          </div>
          <div>
            <div data-testid="customer-screen-source" style={{ fontWeight: 600 }}>
              {screen ? SOURCE_LABELS[source] || SOURCE_LABELS.none : loadFailed ? 'Not available' : 'Loading…'}
            </div>
            <div className="d-flex gap-2 mt-2 flex-wrap">
              <button
                type="button"
                className="btn btnPrimary"
                disabled={controlsDisabled}
                onClick={() => fileInput.current && fileInput.current.click()}
              >
                {busy ? 'Working…' : 'Upload a different logo'}
              </button>
              {source === 'custom' && (
                <button type="button" className="btn btnLight" disabled={controlsDisabled} onClick={handleRemove}>
                  Remove custom logo
                </button>
              )}
            </div>
            <input
              ref={fileInput}
              type="file"
              accept="image/png,image/jpeg"
              data-testid="customer-screen-file"
              style={{ display: 'none' }}
              onChange={handleFile}
            />
          </div>
        </div>

        {!online && (
          <p className="mt-3 mb-0" role="status" style={{ fontSize: 13 }}>
            You are offline. Connect to the internet to change the customer screen logo.
          </p>
        )}
        {loadFailed && online && (
          <p className="mt-3 mb-0" role="status" style={{ fontSize: 13 }}>
            Customer screen settings are not available from the server right now, so the default
            POS icon is used.
          </p>
        )}
        {error && (
          <p className="mt-3 mb-0 text-danger" role="alert" style={{ fontSize: 13 }}>
            {error}
          </p>
        )}
      </div>
      <ConfirmModalRenderer />
    </div>
  );
};

export default CustomerScreenCard;
